import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { setRShieldEnabled, storeRShieldOptions, RSHIELD_WHEN_LIMIT_REACHED, FOCUS_LOSS_COMBINED_TYPE, splitFocusLossValue, } from "../../../../StateManagement/actions";
/**
 * RShield settings hook.
 *
 * Kept as its own module so RShield logic never mixes with the ID Leakers logic
 * in useIdentifyLeakers.js. The two features do share one network call - the
 * GetContentSecuritySettingDetails response carries both - but nothing else:
 * ID Leakers is resolved at user level, RShield at company level (the API
 * resolves it from the sender's CompanyReferenceKey and returns the result as
 * isRShieldEnabled).
 *
 * Pass the response from GetContentSecuritySettingDetails to applyRShieldSetting.
 */
export const useRShield = () => {
    const dispatch = useDispatch();
    const companyRShieldOptionsRef = useRef(null);
    const applyRShieldSetting = useCallback((response) => {
        try {
            const isEnabled =
                response?.isRShieldEnabled === true ||
                response?.isRShieldEnabled === "true";

            dispatch(setRShieldEnabled(isEnabled));
            const commonSettings = response?.setting?.[0]?.commonContentSecuritySetting;
            if (Array.isArray(commonSettings)) {
                const getValue = (type) =>
                    commonSettings.find((item) => item.type === type)?.keyValue;
                const toBool = (value) => value === true || value === "true";
                const normalize = (str) => (str || "").toString().trim().toLowerCase().replace(/[\s-]+/g, "");

                const focusLoss = splitFocusLossValue(getValue(FOCUS_LOSS_COMBINED_TYPE));
                const whenLimitReachedRaw = getValue("WhenLimitisReached");
                const normalizedWhenLimitReached = normalize(whenLimitReachedRaw);

                const computedOptions = {
                    focusProtection: toBool(getValue("FocusProtection")),
                    resumeOnFullScreen: toBool(getValue("ResumeOnFullScreen")),
                    focusLossLimit: focusLoss.enabled === "true",
                    focusLossLimitValue: Number(focusLoss.limit),
                    whenLimitReached: normalizedWhenLimitReached === normalize(RSHIELD_WHEN_LIMIT_REACHED.REAUTHENTICATE)
                        ? RSHIELD_WHEN_LIMIT_REACHED.REAUTHENTICATE
                        : RSHIELD_WHEN_LIMIT_REACHED.END_SESSION,
                    banReader: toBool(getValue("BanReader")),
                    notifySender: toBool(getValue("NotifySender")),
                };
                companyRShieldOptionsRef.current = computedOptions;
                dispatch(storeRShieldOptions(computedOptions));
            }
            return isEnabled;
        } catch (error) {
            console.error('Error in applyRShieldSetting:', error);
            dispatch(setRShieldEnabled(false));
            return false;
        }
    }, [dispatch]);

    return { applyRShieldSetting, companyRShieldOptionsRef };
};

export default useRShield;
